import * as React from "react";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled } from "@mui/material/styles";

type ViewType = "profit" | "turnover";

interface ApiItem {
    region?: string;
    profit?: number | null;
    posTurnoverActual?: number | null;
}

interface DashboardData {
    listOfData?: ApiItem[];
}

/* modern color palette */
const COLORS = [
    "#4F46E5",
    "#06B6D4",
    "#22C55E",
    "#F59E0B",
    "#EF4444",
    "#8B5CF6",
    "#EC4899",
];

/* center label */

const CenterText = styled("text")(() => ({
    fontSize: 22,
    fontWeight: 600,
    textAnchor: "middle",
    dominantBaseline: "central",
}));

function CenterLabel({ children }: { children: React.ReactNode }) {
    const { width, height, left, top } = useDrawingArea();

    return (
        <CenterText x={left + width / 2} y={top + height / 2}>
            {children}
        </CenterText>
    );
}

export function RegionPieChart({
    dashboardData,
}: {
    dashboardData: DashboardData;
}) {
    const [view, setView] = React.useState<ViewType>("profit");

    const list = dashboardData?.listOfData ?? [];

    /* group by region */

    const grouped = React.useMemo(() => {
        const map: Record<string, number> = {};

        list.forEach((item) => {
            const region = item.region ?? "Unknown";

            const value =
                view === "profit"
                    ? item.profit ?? 0
                    : item.posTurnoverActual ?? 0;

            if (!map[region]) map[region] = 0;

            map[region] += value;
        });

        return map;
    }, [list, view]);

    var profitPieData = React.useMemo(() => {
        const map: Record<string, number> = {};

        list.forEach((item) => {
            const region = item.region ?? "Unknown";

            const value =
                view === "profit"
                    ? item.profit ?? 0
                    : item.posTurnoverActual ?? 0;

            if (!map[region]) map[region] = 0;

            map[region] += value;
        });

        return map;
    }, [list]);

    const regions = Object.keys(grouped);

    const total = regions.reduce((sum, r) => sum + grouped[r], 0);

    const data = regions.map((region, i) => ({
        id: region,
        label: region,
        value: grouped[region],
        color: COLORS[i % COLORS.length],
    }));

    React.useEffect(() => {
        console.log("Grouped Data:", data);
    }, [data]);

    const handleChange = (
        _: React.MouseEvent<HTMLElement>,
        newValue: ViewType | null
    ) => {
        if (newValue) setView(newValue);
    };

    const innerRadius = 80;
    const middleRadius = 160;

    return (
        <Box sx={{ width: "100%", textAlign: "center" }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                Bölgesel Performans
            </Typography>

            <ToggleButtonGroup
                value={view}
                exclusive
                onChange={handleChange}
                size="small"
                sx={{ mb: 2 }}
            >
                <ToggleButton value="profit">POS Karı</ToggleButton>
                <ToggleButton value="turnover">Ciro</ToggleButton>
            </ToggleButtonGroup>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <PieChart
                    height={420}
                    slotProps={{
                        legend: { hidden: true },
                    }}
                    margin={{ left: 80, right: 80 }}
                    series={[
                        {
                            data,
                            innerRadius: innerRadius,
                            outerRadius: middleRadius,
                            paddingAngle: 2,
                            cornerRadius: 6,

                            arcLabel: (item) =>
                                `${item.label}\n${((item.value / total) * 100).toFixed(1)}%`,
                        },
                        {
                            data,
                            innerRadius: middleRadius,
                            outerRadius: middleRadius + 20,
                            paddingAngle: 2,
                            cornerRadius: 6
                        },
                    ]}
                    sx={{
                        [`& .${pieArcLabelClasses.root}`]: {
                            fontSize: 12,
                            fontWeight: 600,
                            fill: "#fff",
                        },
                    }}
                >
                    <CenterLabel>{view === "profit" ? "Kar" : "Ciro"}</CenterLabel>
                </PieChart>
            </Box>
        </Box>
    );
}